import React, { useState } from 'react';
import { Lock, Zap, AlertTriangle, Check, Gift, ChevronDown, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollFadeIn } from './ScrollFadeIn';
interface FinalCTAProps {
  onAddToCart?: () => void;
}
export function FinalCTA({
  onAddToCart
}: FinalCTAProps) {
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  return <section className="scroll-mt-8 py-14 bg-slate-900 relative overflow-hidden" data-id="element-234">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-96 bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-235" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-236">
        <ScrollFadeIn data-id="element-237">
          <h2 className="text-4xl md:text-5xl font-serif text-slate-50 mb-6" data-id="element-238">
            Stop Fighting Your Biology.
            <br data-id="element-239" />
            <span className="text-amber-500 italic" data-id="element-240">
              Start Switching It Off.
            </span>
          </h2>
        </ScrollFadeIn>

        <ScrollFadeIn delay={0.1} data-id="element-241">
          <div className="space-y-4 text-lg text-slate-300 leading-relaxed mb-8" data-id="element-242">
            <p data-id="element-243">
              Everything above — the manual, the kit, the map, and the taper
              plan — is available right now for a single payment of $6.39.
            </p>
            <p data-id="element-244">No subscriptions. No upsells. No "premium tier."</p>
            <p data-id="element-245">
              Just the complete system, delivered instantly to your inbox the
              moment you check out.
            </p>
          </div>
        </ScrollFadeIn>

        {/* Urgency warning */}
        <ScrollFadeIn delay={0.15} data-id="element-246">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-10 text-left" data-id="element-247">
            <div className="flex items-start gap-3" data-id="element-248">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" data-id="element-249" />
              <div className="text-sm text-slate-300 leading-relaxed space-y-2" data-id="element-250">
                <p className="text-slate-50 font-medium" data-id="element-251">
                  One thing to keep in mind:
                </p>
                <p data-id="element-252">
                  The bonus stack is included at this price as part of a limited
                  launch period. Once the launch window closes, the bonuses will
                  either be sold separately or removed from the offer entirely.
                </p>
                <p className="text-amber-400 font-medium" data-id="element-253">
                  If you're reading this page, the offer is still active.
                </p>
              </div>
            </div>
          </div>
        </ScrollFadeIn>

        <ScrollFadeIn delay={0.2} data-id="element-254">
          <div id="final-cta" className="scroll-mt-4 bg-slate-800 border border-slate-700 rounded-2xl p-8 md:p-12 shadow-2xl" data-id="element-255">
            <div className="mb-6" data-id="element-256">
              <span className="text-slate-400 uppercase tracking-widest text-sm font-semibold" data-id="element-257">
                Digital Access
              </span>
              <div className="mt-3 flex items-center justify-center gap-3" data-id="element-258">
                <span className="text-2xl text-slate-500 line-through font-medium" data-id="element-259">
                  $148
                </span>
                <span className="text-5xl font-bold text-slate-50" data-id="element-260">$6.39</span>
              </div>
              <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30" data-id="element-261">
                <span className="text-emerald-400 text-sm font-semibold tracking-wide" data-id="element-262">
                  96% OFF — Launch Price
                </span>
              </div>
            </div>

            {/* Bonus stack visual */}
            <div className="border-t border-slate-700 pt-6 mb-6" data-id="element-263">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-4" data-id="element-264">
                What's included:
              </p>

              {/* Main eBook — prominent */}
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-900/60 border border-amber-500/20 mb-4" data-id="element-265">
                <img src="https://cdn.magicpatterns.com/uploads/7wUxyExPymVP8TFY7ndcVW/The-Cortisol-Switch-Logo.png" alt="The Cortisol Switch eBook" className="w-20 h-auto rounded-lg shadow-md shrink-0" data-id="element-266" />
                <div className="text-left" data-id="element-267">
                  <span className="text-sm text-slate-50 font-semibold" data-id="element-268">
                    The Cortisol Switch
                  </span>
                  <p className="text-xs text-slate-400" data-id="element-269">
                    Complete eBook — The full protocol
                  </p>
                </div>
              </div>

              {/* Bonuses — row below */}
              <div className="grid grid-cols-3 gap-3" data-id="element-270">
                {/* Bonus 1 */}
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-271">
                  <img src="https://cdn.magicpatterns.com/uploads/rcZcghPkokYckJZzRYhPcJ/3AM-Rescue-Kit.png" alt="The 3AM Rescue Kit" className="w-14 h-auto rounded shadow-md" data-id="element-272" />
                  <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-273">
                    3AM Rescue Kit
                  </span>
                  <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-274">
                    <Gift className="w-3 h-3" data-id="element-275" /> FREE
                  </span>
                </div>

                {/* Bonus 2 */}
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-276">
                  <img src="https://cdn.magicpatterns.com/uploads/aDH1GgafDem32o1ZEft9G8/Cortisol-Safe-Grocery-Map.png" alt="Cortisol-Safe Grocery Map" className="w-14 h-auto rounded shadow-md" data-id="element-277" />
                  <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-278">
                    Grocery Map
                  </span>
                  <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-279">
                    <Gift className="w-3 h-3" data-id="element-280" /> FREE
                  </span>
                </div>

                {/* Bonus 3 */}
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-700/50" data-id="element-281">
                  <img src="https://cdn.magicpatterns.com/uploads/cuSNPLuFnNdPTDBYMEMsKM/Coffee-Exit-Plan.png" alt="The Coffee Exit Plan" className="w-14 h-auto rounded shadow-md" data-id="element-282" />
                  <span className="text-xs text-slate-50 font-medium text-center leading-tight" data-id="element-283">
                    Coffee Exit Plan
                  </span>
                  <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-1" data-id="element-284">
                    <Gift className="w-3 h-3" data-id="element-285" /> FREE
                  </span>
                </div>
              </div>

              {/* Read more toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors font-medium" data-id="element-286">
                {showBonusDetails ? 'Show less' : 'Read more about the bonuses'}
                <motion.span animate={{
                rotate: showBonusDetails ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className="inline-flex" data-id="element-287">
                  <ChevronDown className="w-4 h-4" data-id="element-288" />
                </motion.span>
              </button>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-289">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: [0.21, 0.47, 0.32, 0.98]
              }} className="overflow-hidden" data-id="element-290">
                    <div className="pt-6 space-y-6 text-left" data-id="element-291">
                      {/* Bonus 1 — 3AM Rescue Kit */}
                      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-292">
                        <div data-id="element-293">
                          <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-294">
                            <Gift className="w-3.5 h-3.5" data-id="element-295" />
                            <span data-id="element-296">Bonus #1</span>
                          </div>
                          <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-297">
                            The 3AM Rescue Kit
                          </h4>
                          <p className="text-slate-400 text-xs" data-id="element-298">
                            Fast-Back-to-Sleep Protocol —{' '}
                            <span className="text-emerald-400 font-semibold" data-id="element-299">
                              FREE
                            </span>
                          </p>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed" data-id="element-300">
                          A decision-based rapid-response system for when you
                          wake at 3AM. Instead of guessing what to do
                          half-asleep, follow a simple 3-route decision tree:
                        </p>
                        <div className="space-y-2" data-id="element-301">
                          <div className="flex items-start gap-2.5" data-id="element-302">
                            <div className="w-4 h-4 rounded-full bg-blue-400 shrink-0 mt-0.5" data-id="element-303" />
                            <p className="text-slate-300 text-xs" data-id="element-304">
                              <span className="text-slate-50 font-medium" data-id="element-305">
                                Route A:
                              </span>{' '}
                              Heart racing — 3-step physical reset
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-306">
                            <div className="w-4 h-4 rounded-full bg-amber-400 shrink-0 mt-0.5" data-id="element-307" />
                            <p className="text-slate-300 text-xs" data-id="element-308">
                              <span className="text-slate-50 font-medium" data-id="element-309">
                                Route B:
                              </span>{' '}
                              Real hunger — rescue snack rules
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-310">
                            <div className="w-4 h-4 rounded-full bg-emerald-400 shrink-0 mt-0.5" data-id="element-311" />
                            <p className="text-slate-300 text-xs" data-id="element-312">
                              <span className="text-slate-50 font-medium" data-id="element-313">
                                Route C:
                              </span>{' '}
                              Mind looping — 4-line journal script
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1.5" data-id="element-314">
                          {['60-second decision tree (usable half-asleep)', '4-7-8 Vagal Brake sequence', 'Rescue snack rules', 'Anti-rumination script', '7 mistakes that keep you awake', 'Printable nightstand one-pager'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-315">
                              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-316" />
                              <span className="text-slate-300 text-xs" data-id="element-317">
                                {item}
                              </span>
                            </div>)}
                        </div>
                      </div>

                      {/* Bonus 2 — Grocery Map */}
                      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-318">
                        <div data-id="element-319">
                          <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-320">
                            <Gift className="w-3.5 h-3.5" data-id="element-321" />
                            <span data-id="element-322">Bonus #2</span>
                          </div>
                          <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-323">
                            Cortisol-Safe Grocery Map
                          </h4>
                          <p className="text-slate-400 text-xs" data-id="element-324">
                            Done-for-You Shopping List + Night Staples (14 Days)
                            —{' '}
                            <span className="text-emerald-400 font-semibold" data-id="element-325">
                              FREE
                            </span>
                          </p>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed" data-id="element-326">
                          A shopping system — not a meal plan. One trip to the
                          store sets you up for two full weeks of calmer
                          evenings and steadier sleep.
                        </p>
                        <div className="space-y-1.5" data-id="element-327">
                          {['12 Night Staples for blood sugar stability', 'Aisle-by-aisle shopping list', 'Cortisol-Safe Dinner Formula', '14 simple dinner + snack combos (10-15 min)', 'Dairy-free, gluten-free & vegetarian swaps', '"Better to Avoid" trigger food list', '"Your First Cart" fill-in template'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-328">
                              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-329" />
                              <span className="text-slate-300 text-xs" data-id="element-330">
                                {item}
                              </span>
                            </div>)}
                        </div>
                      </div>

                      {/* Bonus 3 — Coffee Exit Plan */}
                      <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-5 space-y-4" data-id="element-331">
                        <div data-id="element-332">
                          <div className="inline-flex items-center gap-2 text-amber-500 text-xs font-semibold uppercase tracking-widest mb-2" data-id="element-333">
                            <Gift className="w-3.5 h-3.5" data-id="element-334" />
                            <span data-id="element-335">Bonus #3</span>
                          </div>
                          <h4 className="text-lg font-serif text-slate-50 mb-1" data-id="element-336">
                            The Coffee Exit Plan
                          </h4>
                          <p className="text-slate-400 text-xs" data-id="element-337">
                            2-Week Caffeine Taper Without the Crash —{' '}
                            <span className="text-emerald-400 font-semibold" data-id="element-338">
                              FREE
                            </span>
                          </p>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed" data-id="element-339">
                          Breaks the caffeine-cortisol loop without quitting
                          cold turkey. 14 days, 3 simple phases:
                        </p>
                        <div className="space-y-2" data-id="element-340">
                          <div className="flex items-start gap-2.5" data-id="element-341">
                            <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-342" />
                            <p className="text-slate-300 text-xs" data-id="element-343">
                              <span className="text-slate-50 font-medium" data-id="element-344">
                                Days 1-3:
                              </span>{' '}
                              Timing reset
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-345">
                            <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-346" />
                            <p className="text-slate-300 text-xs" data-id="element-347">
                              <span className="text-slate-50 font-medium" data-id="element-348">
                                Days 4-7:
                              </span>{' '}
                              Gentle taper
                            </p>
                          </div>
                          <div className="flex items-start gap-2.5" data-id="element-349">
                            <Calendar className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" data-id="element-350" />
                            <p className="text-slate-300 text-xs" data-id="element-351">
                              <span className="text-slate-50 font-medium" data-id="element-352">
                                Days 8-14:
                              </span>{' '}
                              Stabilize
                            </p>
                          </div>
                        </div>
                        <div className="space-y-1.5" data-id="element-353">
                          {['4 Timing Rules for jitters & crashes', 'Cut-off time calculator', '10 non-caffeine energy boosters', '2PM Crash Protocol (5 min fix)', 'Headache & irritability troubleshooting', '14-day daily tracker'].map(item => <div key={item} className="flex items-start gap-2" data-id="element-354">
                              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" data-id="element-355" />
                              <span className="text-slate-300 text-xs" data-id="element-356">
                                {item}
                              </span>
                            </div>)}
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Confirmation line */}
            <div className="flex items-center justify-center gap-2 mb-6" data-id="element-357">
              <Check className="w-5 h-5 text-emerald-400" data-id="element-358" />
              <span className="text-emerald-400 font-semibold" data-id="element-359">
                YES — I want The Cortisol Switch + all 3 bonuses
              </span>
            </div>

            <a href="https://www.recursosencalma.com/cart/45769486663853:1" target="_blank" rel="noopener noreferrer" onClick={onAddToCart} className="cta-pulse w-full sm:w-auto inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-slate-900 bg-amber-500 rounded-xl hover:bg-amber-400 transition-colors duration-300 mb-6" data-id="element-360">
              Download The Cortisol Switch Now
            </a>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6 text-sm text-slate-400 mb-4" data-id="element-361">
              <div className="flex items-center" data-id="element-362">
                <Lock className="w-4 h-4 mr-2 text-emerald-400" data-id="element-363" />
                Secure Checkout
              </div>
              <div className="hidden sm:block text-slate-600" data-id="element-364">•</div>
              <div className="flex items-center" data-id="element-365">
                <Zap className="w-4 h-4 mr-2 text-amber-400" data-id="element-366" />
                Immediate Digital Access
              </div>
            </div>

            <p className="text-xs text-slate-500" data-id="element-367">
              Secure checkout. Immediate digital delivery. 100% satisfaction
              guaranteed.
            </p>
          </div>
        </ScrollFadeIn>
      </div>
    </section>;
}